import { useNavigate } from '@fukuri-admins/hooks/common';
import { useToast } from '@fukuri-admins/ui/common/Toast/hooks';
import { getThreeLetterCode } from '@fukuri-admins/utils/common/cookies';
import { formatDate } from '@fukuri-admins/utils/common/date';
import { AxiosError } from 'axios';
import { useState } from 'react';
import { ROUTES } from '~/utils/routes';
import { useGetClient } from './useGetClient';
import { usePostStressAssessments } from './usePostStressAssessments';

type Deadline = Date | undefined;
type IUseCreateStressAssessment = () => {
  state: {
    userCount: number | undefined;
    deadline: Deadline;
    isButtonDisabled: boolean;
  };
  actions: {
    onDeadlineChange: (value: Deadline) => void;
    onSubmit: () => void;
    onBack: () => void;
  };
};
export const useCreateStressAssessment: IUseCreateStressAssessment = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [deadline, setDeadline] = useState<Deadline>(undefined);
  const threeLetterCode = getThreeLetterCode();

  const { data } = useGetClient(threeLetterCode);
  const { trigger, isMutating } = usePostStressAssessments(threeLetterCode, {
    onSuccess: () => {
      showToast({
        message: 'ストレスチェックを送信しました。',
        status: 'success',
      });
      navigate(ROUTES.STRESS_ASSESSMENTS.INDEX);
    },
    onError: (error: AxiosError<{ errors?: string[] }>) => {
      if (error.response?.status === 422) {
        const errors = error.response?.data?.errors;
        if (errors && errors.length > 0) {
          // 最初のエラーメッセージを表示
          showToast({
            message: errors[0],
            status: 'danger',
          });
          return;
        }
      }

      showToast({
        message: 'ストレスチェックの送信に失敗しました。',
        status: 'danger',
      });
    },
  });

  const handleOnSubmit = () => {
    if (!deadline) return;

    const jstISOString = formatDate({
      date: deadline,
      formatType: "yyyy-MM-dd'T'HH:mm:ssxxx",
    });

    trigger({
      stress_assessment: {
        due_date: jstISOString,
      },
    });
  };

  const onBack = () => navigate(ROUTES.STRESS_ASSESSMENTS.INDEX);

  const userCount = data?.client.non_invited_user_count ?? 0;

  const isButtonDisabled = isMutating || deadline === undefined;

  return {
    state: {
      userCount,
      deadline,
      isButtonDisabled,
    },
    actions: {
      onDeadlineChange: setDeadline,
      onSubmit: handleOnSubmit,
      onBack,
    },
  };
};
